import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { type CustomerView } from "@portal/schemas";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  Textarea,
} from "@portal/ui";
import { useState, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { createCustomer, updateCustomer } from "../../api/client";
import { useInvalidate } from "../../api/queries";
import { LabelWithHint } from "../../components/Hint";
import { InlineError } from "../../components/InlineError";
import { Save } from "lucide-react";

/** 画面用: 別名は1行1件のテキストで入力し、送信時に配列へ変換する。 */
const formSchema = z.object({
  name: z.string().trim().min(1, "顧客名は必須です").max(200),
  aliasesText: z.string().max(5000),
  note: z.string().max(5000),
});
type FormValues = z.infer<typeof formSchema>;

/**
 * 顧客の登録・編集。trigger を渡せばそのボタンで開く。プルダウンから「新しい顧客を登録」で開くときは
 * open / onOpenChange で外から開き、onSaved で登録した顧客を受け取る(2026-10-15)。
 */
export function CustomerFormDialog({
  customer,
  trigger,
  open: openProp,
  onOpenChange,
  defaultName,
  onSaved,
}: {
  customer?: CustomerView;
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** 新規のとき、顧客名に最初から入れておく値(件名から推定した顧客名など)。 */
  defaultName?: string;
  onSaved?: (customer: CustomerView) => void;
}) {
  const [innerOpen, setInnerOpen] = useState(false);
  const open = openProp ?? innerOpen;
  const setOpen = onOpenChange ?? setInnerOpen;
  const invalidate = useInvalidate();
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    values: {
      name: customer?.name ?? defaultName ?? "",
      aliasesText: customer?.aliases.join("\n") ?? "",
      note: customer?.note ?? "",
    },
  });
  const save = useMutation({
    mutationFn: (v: FormValues) => {
      const body = {
        name: v.name,
        aliases: v.aliasesText
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean),
        note: v.note,
      };
      return customer
        ? updateCustomer(customer.id, { ...body, revision: customer.revision })
        : createCustomer(body);
    },
    onSuccess: async (res) => {
      await invalidate.customers();
      await invalidate.projects();
      setOpen(false);
      if (!customer) form.reset();
      onSaved?.(res.customer);
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent>
        <form
          onSubmit={form.handleSubmit((v) => save.mutate(v))}
          noValidate
          className="flex flex-col gap-4"
        >
          <DialogHeader>
            <DialogTitle>{customer ? "顧客を編集" : "顧客を登録"}</DialogTitle>
            <DialogDescription className="sr-only">
              顧客名・別名・備考を入力します。
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="customer-name">顧客名</Label>
            <Input id="customer-name" {...form.register("name")} />
            {form.formState.errors.name && (
              <p className="text-xs text-destructive">{form.formState.errors.name.message}</p>
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            {/* 説明はラベルの右のヒントに入れる(ユーザー要望 2026-10-15) */}
            <LabelWithHint
              htmlFor="customer-aliases"
              hint="1 行に 1 件ずつ入力します。表記揺れの吸収に使います。"
            >
              別名
            </LabelWithHint>
            <Textarea id="customer-aliases" rows={3} {...form.register("aliasesText")} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="customer-note">備考</Label>
            <Textarea id="customer-note" rows={3} {...form.register("note")} />
          </div>
          <InlineError error={save.error} />
          <DialogFooter>
            <Button type="submit" disabled={save.isPending}>
              <Save />
              保存
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
